import { supabase } from '@/integrations/supabase/client';
import { writeLanded } from '@/lib/supabaseWrite';

/**
 * Aligne `profiles.language` sur la langue réellement affichée à l'écran.
 *
 * POURQUOI : `handle_new_user` construit la langue avec
 * `COALESCE(meta->>'language', 'en')`. L'inscription par courriel passe bien
 * `language` dans les métadonnées, mais `supabase.auth.signInWithOAuth` NE SAIT
 * PAS transmettre `options.data` : un compte créé avec Google naît donc toujours
 * en anglais, même sur un salon francophone. Mesuré en production le 2026-08-18,
 * sur un compte recréé pour l'occasion : `language = 'en'` à la création, corrigé
 * en `'fr'` seulement trois minutes plus tard, à la première réservation.
 *
 * Corrigé côté front et NON dans le repli SQL, qui serait faux pour un salon
 * anglophone : le déclencheur n'a aucun moyen de connaître la langue de l'écran.
 *
 * ⚠️ Ne jette JAMAIS : appelée sur un chemin de connexion et après une
 * réservation déjà réussie, elle ne doit bloquer ni l'un ni l'autre.
 *
 * ⚠️ `.select()` n'est pas décoratif : un refus RLS rend 0 ligne avec
 * `error === null`. Sans lui, « refusé en silence » passerait pour « écrit ».
 */
export async function alignProfileLanguage(
  userId: string,
  language: string,
): Promise<'written' | 'skipped' | 'refused'> {
  if (!userId || !language) return 'skipped';
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('language')
      .eq('id', userId)
      .maybeSingle();

    // Aucun profil lisible, ou langue déjà la bonne : rien à faire.
    if (!profile || profile.language === language) return 'skipped';

    const { data } = await supabase
      .from('profiles')
      .update({ language })
      .eq('id', userId)
      .select('id');
    return writeLanded(data) ? 'written' : 'refused';
  } catch {
    return 'refused';
  }
}
